import { Element, useNode } from "@craftjs/core";
import * as React from "react";
import { Actions } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Button from "@/components/editor/primitives/button/button-component";
import RichText from "@/components/editor/primitives/rich-text";
import { whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import useEvent from "@/hooks/use-event";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";

import {
  DEFAULT_BUTTON_TEXT,
  DEFAULT_HEADING_TEXT,
  DEFAULT_SUBHEADING_TEXT,
} from "./constants";
import {
  ButtonWrapper,
  Content,
  Image,
  ImageWrapper,
  Logo,
  LogoWrapper,
  TextContent,
  Wrapper,
} from "./header-option-4-component-styles";

type HeaderOption4Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  isLogoShowing?: boolean;
  logo?: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return (
    <HeaderSettings
      optimalImageSize="1440x780px"
      backgroundImageSize={{
        width: "240",
        height: "130",
      }}
    />
  );
};

export default function HeaderOption4(props: HeaderOption4Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();
  const { event } = useEvent();

  return (
    <Wrapper ref={connect} id={nodeId}>
      <ImageWrapper>
        <Image
          imageFit={props.imageFit}
          $position={props.backgroundPosition || "center center"}
          src={props.backgroundImage}
          alt={props.backgroundImageAlt || ""}
        />
      </ImageWrapper>
      <Content>
        {props.isLogoShowing && (
          <LogoWrapper>
            <Logo
              draggable="false"
              src={props.logo || whiteLogo}
              alt="Header Logo"
              style={{
                width: formatLogoSize(props.logoSize),
                height: "auto",
              }}
              isLogoResized={!isNaN(props.logoSize)}
            />
          </LogoWrapper>
        )}
        <TextContent>
          <Element
            id="heading"
            is={RichText}
            text={`<h2 style="text-align:center;">${
              event.name || DEFAULT_HEADING_TEXT
            }</h2>`}
          />
          <Element
            id="subheading"
            is={RichText}
            text={`<p style="text-align:center;">${DEFAULT_SUBHEADING_TEXT}</p>`}
          />
        </TextContent>
        <Element
          id="cta-text"
          is={RichText}
          text={`<h5 style="text-align:center;">${humanizeDateRange(
            event.time_start,
            event.time_end,
            event.standard_timezone
          ).join(" - ")}</h5>`}
        />
        <ButtonWrapper>
          <Element
            id="cta-button"
            is={Button}
            text={DEFAULT_BUTTON_TEXT}
            action={{
              kind: Actions.TicketSelector,
            }}
          />
        </ButtonWrapper>
      </Content>
    </Wrapper>
  );
}

HeaderOption4.craft = {
  custom: {
    displayName: "Header option #4",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
